(() => {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let observer;
  const twinPathFragment = '/products/yellow-kangaroo-spray-twinpack';
  let lastAddToCartKey = '';
  let lastAddToCartAt = 0;

  const getPostHog = () => {
    if (!window.posthog || typeof window.posthog.capture !== 'function') return null;
    return window.posthog;
  };

  const cleanProperties = (properties) =>
    Object.fromEntries(
      Object.entries(properties).filter(([, value]) => value !== null && value !== undefined && value !== '')
    );

  const centsToAmount = (valueInCents) => {
    const numericValue = Number(valueInCents);
    if (!Number.isFinite(numericValue)) return null;
    return Number((numericValue / 100).toFixed(2));
  };

  const fetchCartSnapshot = async () => {
    const response = await fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });

    if (!response.ok) {
      throw new Error(`cart fetch failed: ${response.status}`);
    }

    return response.json();
  };

  const capturePostHogEvent = (eventName, properties) => {
    const posthog = getPostHog();
    if (!posthog) return;

    posthog.capture(eventName, cleanProperties(properties));
  };

  const buildCartLineProperties = (lineItem, cart, quantity, source) =>
    cleanProperties({
      source,
      product_id: lineItem ? String(lineItem.product_id) : null,
      variant_id: lineItem ? String(lineItem.variant_id) : null,
      sku: lineItem?.sku || null,
      product_name: lineItem?.product_title || null,
      variant_title: lineItem?.variant_title && lineItem.variant_title !== 'Default Title' ? lineItem.variant_title : null,
      product_type: lineItem?.product_type || null,
      product_handle: lineItem?.handle || null,
      quantity,
      price: centsToAmount(lineItem?.final_price ?? lineItem?.price),
      value: centsToAmount((lineItem?.final_price ?? lineItem?.price ?? 0) * quantity),
      currency: cart?.currency || window.Shopify?.currency?.active || 'EUR',
      cart_token: cart?.token || null,
      cart_item_count: Number(cart?.item_count) || null,
      page_path: window.location.pathname,
      page_url: window.location.href,
    });

  const handleAddToCartTracking = async (event) => {
    const detail = event?.detail || {};
    const data = detail.data || {};
    const source = data.source || '';

    if (data.didError) return;
    if (source !== 'product-form-component') return;

    const quantity = Number(data.itemCount) || 1;
    const variantId = String(data.variantId || detail.sourceId || '');
    if (!variantId) return;

    const dedupeKey = `${source}:${variantId}:${quantity}`;
    const now = Date.now();
    if (dedupeKey === lastAddToCartKey && now - lastAddToCartAt < 1500) return;

    lastAddToCartKey = dedupeKey;
    lastAddToCartAt = now;

    try {
      const cart = await fetchCartSnapshot();
      const matchingLine = Array.isArray(cart.items)
        ? cart.items.find((item) => String(item.variant_id) === variantId)
        : null;

      capturePostHogEvent('add_to_cart', buildCartLineProperties(matchingLine, cart, quantity, source));
    } catch (error) {
      console.error('PostHog add_to_cart tracking failed', error);
    }
  };

  const reveal = (root = document) => {
    const revealItems = root.querySelectorAll ? root.querySelectorAll('.yk-reveal') : [];
    if (!revealItems.length) return;

    // In Shopify theme editor, sections are re-rendered dynamically; reveal immediately to avoid "missing" content.
    if (window.Shopify && window.Shopify.designMode) {
      revealItems.forEach((item) => item.classList.add('is-visible'));
      return;
    }

    if (!prefersReducedMotion && 'IntersectionObserver' in window) {
      if (!observer) {
        observer = new IntersectionObserver(
          (entries, obs) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add('is-visible');
              obs.unobserve(entry.target);
            });
          },
          { threshold: 0.16, rootMargin: '0px 0px -8% 0px' }
        );
      }

      revealItems.forEach((item) => {
        if (item.classList.contains('is-visible')) return;
        observer.observe(item);
      });
      return;
    }

    revealItems.forEach((item) => item.classList.add('is-visible'));
  };

  const ensureTwinComparePrice = (root = document) => {
    if (!window.location.pathname.includes(twinPathFragment)) return;

    const priceContainer = root.querySelector?.('.price-container--product');
    if (!priceContainer) return;
    if (priceContainer.querySelector('.compare-at-price')) return;

    const compareGroup = document.createElement('span');
    compareGroup.setAttribute('role', 'group');
    compareGroup.innerHTML =
      '<span class="visually-hidden">Regulaerer Preis&nbsp;</span><span class="compare-at-price">€49,00</span>';
    priceContainer.appendChild(compareGroup);
  };

  reveal(document);
  ensureTwinComparePrice(document);
  document.addEventListener('cart:update', handleAddToCartTracking);

  document.addEventListener('shopify:section:load', (event) => {
    reveal(event.target);
    ensureTwinComparePrice(event.target);
  });
  document.addEventListener('shopify:section:reorder', () => {
    reveal(document);
    ensureTwinComparePrice(document);
  });
})();
